


































































import { computed, defineComponent, Ref, ref, toRefs, watchEffect } from "@vue/composition-api";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { MessageAttachment } from "@scrile/api-provider/dist/api/MessagesProvider";
import { isVideoFile } from "@/lib/fileHelpers";
import { BlockingStatus } from "@/types";
import BaseIcon from "@/components/BaseIcon";
import FullScreen from "@/components/FullScreen";
import GalleryTotalItems from "@/components/GalleryTotalItems";
import MessageGalleryLocked from "./MessageGalleryLocked.vue";
import MessageMediaItem from "./MessageMediaItem.vue";

const useAttachmentAvailability = (isAvailable: Ref<boolean>, isAuthor: Ref<boolean>) => {
  const clicked = ref(false);
  const showBlur = computed(() => !clicked.value && isAuthor.value && !isAvailable.value);
  const contentAvailable = computed(() => {
    if (isAuthor.value) return !showBlur.value;
    return isAvailable.value;
  });

  watchEffect(() => {
    if (isAvailable.value) {
      clicked.value = false;
    }
  });
  return {
    contentAvailable,
    clicked,
    showBlur,
  };
};

interface FileListInfo {
  accessed: boolean;
  file: ContentFile | FileData | null;
  id: string;
}

export default defineComponent({
  name: "MessageMediaGallery",
  components: { MessageMediaItem, GalleryTotalItems, BaseIcon, FullScreen, MessageGalleryLocked },
  props: {
    attachment: { type: Array as () => MessageAttachment[], required: true },
    price: { type: String, required: true },
    isAuthor: { type: Boolean, required: true },
    isAvailable: { type: Boolean, required: true },
    blockingStatus: { type: Object as () => BlockingStatus, required: true },
  },
  setup(props, { emit }) {
    const { isAuthor, isAvailable } = toRefs(props);
    const { contentAvailable, clicked, showBlur } = useAttachmentAvailability(isAvailable, isAuthor);

    const accessedAttachments = computed(() => {
      const attachments = props.attachment.sort((a, b) =>
        a.file.fileId.fileNumber < b.file.fileId.fileNumber ? -1 : 1
      );
      if (!contentAvailable.value) {
        return attachments.filter(f => f.publicAccess);
      }
      return attachments;
    });

    const listLength = computed(
      () => accessedAttachments.value.length + (props.attachment.length > accessedAttachments.value.length ? 1 : 0)
    );
    const privateAttachments = computed(() => props.attachment.filter(f => !f.publicAccess));
    const itemIndex = ref(0);
    const scalable = computed(() =>
      isAvailable.value && props.attachment[itemIndex.value]?.file
        ? !isVideoFile(props.attachment[itemIndex.value].file)
        : false
    );

    const renderFileList = computed<FileListInfo[]>(() => {
      const lockedFile: FileListInfo[] = [];
      if (privateAttachments.value.length > 0 && !contentAvailable.value) {
        lockedFile.push({ accessed: false, file: null, id: privateAttachments.value[0].file.fileId.subjectId });
      }
      return accessedAttachments.value
        .map(
          attach =>
            ({ accessed: true, file: attach.file, id: attach.file.fileId.fileNumber.toString() } as FileListInfo)
        )
        .concat(lockedFile);
    });

    const galleryItemStyle = computed(() => {
      const list = accessedAttachments.value.length > 0 ? accessedAttachments.value : privateAttachments.value;
      const attachment = list.find(a => !!a);
      const width = Number(attachment?.file.metadata.width ?? 0);
      const height = Number(attachment?.file.metadata.height ?? 0);

      if (width > 0 && height > 0) {
        const MAX_RATIO = 150;
        const MIN_RATIO = 45;
        const fileRatio = Math.ceil((height / width) * 100);

        return {
          "padding-bottom": `${Math.min(Math.max(fileRatio, MIN_RATIO), MAX_RATIO)}%`,
        };
      }
      return {};
    });

    const onUnlock = () => {
      if (props.isAuthor) {
        clicked.value = true;
      } else {
        emit("unlock");
      }
    };

    return {
      clicked,
      accessedAttachments,
      privateAttachments,
      galleryItemStyle,
      onUnlock,
      listLength,
      contentAvailable,
      showBlur,
      itemIndex,
      scalable,
      renderFileList,
    };
  },
});
